import _ from "lodash";
import React from "react";
import { connect } from "react-redux";
import { graphql, compose } from "lib/apolloClient";
import withLoader from "UIComponents/GqlWithLoader";
import Loading from "UIComponents/Loading";
import I18nHOC from "UIComponents/I18nHOC";
import withPusherBind from "UIComponents/PusherConsumerHoc";
import FullScreenLoader from "UIComponents/FullScreenLoader";

import { getNotificationsQuery } from "modules/CommonQuery";
import { getNotificationListFromCache } from "modules/CommonGraphqlHelpers";
import { colors } from "Constants";

import { setNotificationCount } from "Teacher/modules/TeacherModule";
import { setCurrentCurriculumProgram } from "Platform/modules/PlatformModule";

import classes from "./ListContainer.scss";
import { navigate } from "modules/NavigationModule";
import { checkScrollAtBottom } from "services/checkScrollAtBottom";
import {
  IcnNotiAddedCollaborator,
  IcnNotiAddedGoal,
  IcnNotiAddedPolicy,
  IcnNotiAddedResource,
  IcnNotiAnnouncement,
  IcnNotiParentConnected,
  IcnNotiPostComment,
  IcnNotiPostLike,
  IcnNotiReportEvaluation,
  IcnNotiReportRivision,
  IcnNotiAddedPost,
  IcnNotiAddStudent,
  MessageIcon,
  SubmissionIcon,
  FeedbackNotificationIcon,
  IcnNotiSubmissionReturned,
  IcnNotiEvaluationCompleted,
  IcnNotiVideoMeet,
  CameraIcon,
  NoteIconV2,
  FileIconSvg,
  NewLinkIcon,
  VoiceIcon,
  VideoIconSvg,
  LEFilledIcon,
  QuickTaskFilledIcon,
  Formative,
  Summative,
} from "SvgComponents";
import {
  ClockTimerOutlined,
  WorkbookOutlined,
  StudentShareOutlined,
  PlaceholderFileOutlined,
  FolderOutlined,
  EeOutlined,
  CasOutlined,
  TheoryofKnowledgeOutlined,
  ClipboardPencilLinesOutlined,
  PpOutlined,
  SaaOutlined,
  CpOutlined,
  AttendanceOutlined,
  HourglassOutlined,
  EnvelopeClockOutlined,
  EnvelopeChevronRightOutlined,
  InternalAssessmentOutlined,
  ExternalAssessmentOutlined,
  ImportOutlined,
  StudentOutlined,
  EnvelopeDotFilledOutlined,
} from "@toddle-edu/ds-icons";
import {
  notificationCenterOpenMutation,
  notificationCenterReadMutation,
} from "AppComponents/NotificationCenterV2/modules/NotificationCenterV2Modules";
import List from "./List";
import { getUserCoursesBasicDetails } from "Course/modules/CourseModule";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { getUserNotificationCommonCourseIdsMemoize } from "AppComponents/NotificationCenterV2/modules/Utils";

const projectNotificationSubCategries = {
  PERSONAL: {
    svgIcon: <PpOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceTwoD,
  },
  COMMUNITY: {
    svgIcon: <CpOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceOneD,
  },
  SERVICE_AS_ACTION: {
    svgIcon: <SaaOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  DP_TOK_ESSAY: {
    svgIcon: <TheoryofKnowledgeOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  DP_TOK_EXHIBITION: {
    svgIcon: <TheoryofKnowledgeOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  DP_EE: {
    svgIcon: <EeOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  DP_CAS: {
    svgIcon: <CasOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  INTERNAL_ASSESSMENT: {
    svgIcon: <InternalAssessmentOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  EXTERNAL_ASSESSMENT: {
    svgIcon: <ExternalAssessmentOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  DP_G4_PROJECT: {
    svgIcon: <HourglassOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  CP_SERVICE_LEARNING: {
    svgIcon: <HourglassOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  DP_COLLABORATIVE_SCIENCES_PROJECT: {
    svgIcon: <HourglassOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
};

export const notificationTypes = {
  COMMENT: {
    label: "common:comment",
    svgIcon: <IcnNotiPostComment fill={colors.white} width={32} height={32} />,
    bgColor: colors.pink200,
  },
  COMMENT_POST: {
    label: "common:comment",
    svgIcon: <IcnNotiPostComment fill={colors.white} width={32} height={32} />,
    bgColor: colors.pink200,
  },
  COMMENT_ASSESSMENT: {
    label: "common:comment",
    svgIcon: <IcnNotiPostComment fill={colors.white} width={32} height={32} />,
    bgColor: colors.pink200,
  },
  COMMENT_UNIT_PLAN: {
    label: "common:comment",
    svgIcon: <IcnNotiPostComment fill={colors.white} width={32} height={32} />,
    bgColor: colors.pink200,
  },
  ASSIGNMENT_MESSAGE_SENT: {
    label: "notifications:student_message_sent",
    studentLabel: "notifications:teacher_message_sent",
    svgIcon: <MessageIcon fill={colors.white} width={22} height={22} />,
    bgColor: colors.pink200,
  },
  ASSIGNMENT_CREATE: {
    label: "notifications:new_task",
    svgIcon: <IcnNotiAddedPost fill={colors.white} width={32} height={32} />,
    bgColor: colors.blue400,
    subCategories: {
      ASSIGNMENT_RESOURCE: {
        svgIcon: (
          <QuickTaskFilledIcon fill={colors.white} width={24} height={24} />
        ),
        bgColor: colors.grass,
      },
      ASSESSMENT: {
        svgIcon: <LEFilledIcon fill={colors.white} height={24} width={24} />,
        bgColor: colors.violet500,
      },
      le: {
        svgIcon: <LEFilledIcon fill={colors.white} height={24} width={24} />,
        bgColor: colors.violet500,
      },
      fmt: {
        svgIcon: <Formative fill={colors.white} height={24} width={24} />,
        bgColor: colors.yellow500,
      },
      smt: {
        svgIcon: <Summative fill={colors.white} height={24} width={24} />,
        bgColor: colors.teal500,
      },
      qt: {
        svgIcon: (
          <QuickTaskFilledIcon fill={colors.white} width={24} height={24} />
        ),
        bgColor: colors.grass,
      },
      dp_pa: {
        svgIcon: (
          <QuickTaskFilledIcon fill={colors.white} width={24} height={24} />
        ),
        bgColor: colors.grass,
      },
      dp_ep: {
        svgIcon: (
          <QuickTaskFilledIcon fill={colors.white} width={24} height={24} />
        ),
        bgColor: colors.grass,
      },
      worksheet: {
        svgIcon: (
          <QuickTaskFilledIcon fill={colors.white} width={24} height={24} />
        ),
        bgColor: colors.grass,
      },
    },
  },
  ZOOM_MEETING_CREATE: {
    label: "notifications:meeting_invitation",
    svgIcon: <IcnNotiAnnouncement fill={colors.white} width={32} height={32} />,
    bgColor: colors.pink300,
  },
  CALENDAR_EVENT_ITEM: {
    label: "notifications:meeting_invitation",
    svgIcon: <IcnNotiVideoMeet fill={colors.white} width={32} height={32} />,
    bgColor: colors.pink300,
  },
  CALENDAR_EVENT_ITEM_RESCHEDULED: {
    label: "notifications:meeting_invitation",
    svgIcon: <IcnNotiVideoMeet fill={colors.white} width={32} height={32} />,
    bgColor: colors.pink300,
  },
  ASSIGNMENT_GROUP_MESSAGE_SENT: {
    label: "notifications:student_group_message_sent",
    svgIcon: <MessageIcon fill={colors.white} width={22} height={22} />,
    bgColor: colors.pink200,
  },
  REPLIED_MESSAGE: {
    label: "notifications:parent_message_sent",
    svgIcon: <MessageIcon fill={colors.white} width={22} height={22} />,
    bgColor: colors.pink200,
  },
  CHAT_MESSAGE_SENT: {
    label: "notifications:parent_message_sent",
    svgIcon: <MessageIcon fill={colors.white} width={22} height={22} />,
    bgColor: colors.pink200,
  },
  COLLABORATOR: {
    label: "notifications:added_as_collaborator",
    svgIcon: (
      <IcnNotiAddedCollaborator fill={colors.white} width={32} height={32} />
    ),
    bgColor: "#5f2945",
  },
  POST: {
    label: "common:tagged_post",
    bgColor: colors.violet500,
    svgIcon: <IcnNotiAddedPost fill={colors.white} width={32} height={32} />,
    subCategories: {
      NOTE: {
        svgIcon: <NoteIconV2 fill={colors.white} width={24} height={24} />,
        bgColor: colors.violet500,
      },
      WORKBOOK: {
        svgIcon: <WorkbookOutlined size="x-small" variant="on" />,
        bgColor: colors.violet500,
      },
      FILE: {
        svgIcon: <FileIconSvg fill={colors.white} width={20} height={20} />,
        bgColor: colors.violet500,
      },
      LINK: {
        svgIcon: <NewLinkIcon fill={colors.white} width={24} height={24} />,
        bgColor: colors.violet500,
      },
      AUDIO: {
        svgIcon: <VoiceIcon fill={colors.white} width={24} height={22} />,
        bgColor: colors.violet500,
      },
      VIDEO: {
        svgIcon: <VideoIconSvg fill={colors.white} width={24} height={24} />,
        bgColor: colors.violet500,
      },
      IMAGE: {
        svgIcon: <CameraIcon fill={colors.white} width={22} height={22} />,
        bgColor: colors.violet500,
      },
      POST: {
        svgIcon: (
          <IcnNotiAddedPost fill={colors.white} width={32} height={32} />
        ),
        bgColor: colors.violet500,
      },
    },
  },
  POST_APPROVAL: {
    label: "journal:approve_post",
    bgColor: colors.violet500,
    svgIcon: <IcnNotiAddedPost fill={colors.white} width={32} height={32} />,
  },
  POST_APPROVAL_WITH_COMMENT: {
    label: "notifications:notif_title_approval_with_comment",
    bgColor: colors.violet500,
    svgIcon: <IcnNotiAddedPost fill={colors.white} width={32} height={32} />,
  },
  POST_REJECT_WITH_COMMENT: {
    label: "notifications:notif_title_reject_with_comment",
    bgColor: colors.violet500,
    svgIcon: <IcnNotiAddedPost fill={colors.white} width={32} height={32} />,
  },
  STUDENT_POST_APPROVED: {
    label: "common:new_post",
    bgColor: colors.violet500,
    svgIcon: <IcnNotiAddedPost fill={colors.white} width={32} height={32} />,
  },
  STUDENT_POST_PUBLISHED: {
    label: "common:new_post",
    bgColor: colors.violet500,
    svgIcon: <IcnNotiAddedPost fill={colors.white} width={32} height={32} />,
  },
  GOAL_CREATED: {
    label: "notifications:added_a_goal",
    svgIcon: <IcnNotiAddedGoal fill={colors.white} width={32} height={32} />,
    bgColor: "#5f2945",
  },
  GOAL_MILESTONE_CREATED: {
    label: "notifications:added_a_milestone",
    svgIcon: <IcnNotiAddedGoal fill={colors.white} width={32} height={32} />,
    bgColor: "#5f2945",
  },
  GOAL_COMMENT: {
    label: "notifications:commented_on_goal",
    svgIcon: <IcnNotiAddedGoal fill={colors.white} width={32} height={32} />,
    bgColor: "#5f2945",
  },
  GOAL_MILESTONE_COMMENT: {
    label: "notifications:commented_on_milestone",
    svgIcon: <IcnNotiAddedGoal fill={colors.white} width={32} height={32} />,
    bgColor: "#5f2945",
  },
  TEACHER_RESOURCE: {
    label: "notifications:added_a_resource",
    svgIcon: (
      <IcnNotiAddedResource fill={colors.white} width={32} height={32} />
    ),
    bgColor: colors.teal200,
  },
  ADMIN_RESOURCE: {
    label: "notifications:added_a_resource",
    svgIcon: (
      <IcnNotiAddedResource fill={colors.white} width={32} height={32} />
    ),
    bgColor: colors.teal200,
  },
  TEACHER_SCHOOL_POLICY: {
    label: "notifications:added_a_policy",
    svgIcon: <IcnNotiAddedPolicy fill={colors.white} width={32} height={32} />,
    bgColor: "#ffab00",
  },
  PARENT_SCHOOL_POLICY: {
    label: "notifications:added_a_policy",
    svgIcon: <IcnNotiAddedPolicy fill={colors.white} width={32} height={32} />,
    bgColor: "#ffab00",
  },
  STUDENT_ADDED: {
    label: "notifications:new_student",
    bgColor: colors.bar,
    svgIcon: <IcnNotiAddStudent fill={colors.white} width={32} height={32} />,
  },
  LEARNING_ENGAGEMENT: {
    label: "notifications:new_le",
    useDefaultImage: true,
  },
  ASSESSMENT: {
    label: "notifications:new_le",
    useDefaultImage: false,
    svgIcon: <LEFilledIcon fill={colors.white} height={24} width={24} />,
    bgColor: colors.violet500,
  },
  GOAL_COMPLETED: {
    label: "notifications:goal_completed",
    svgIcon: <IcnNotiAddedGoal fill={colors.white} width={32} height={32} />,
    bgColor: "#5f2945",
  },
  GOAL_MILESTONE_COMPLETED: {
    label: "notifications:milestone_completed",
    svgIcon: <IcnNotiAddedGoal fill={colors.white} width={32} height={32} />,
    bgColor: "#5f2945",
  },
  STUDENT_ADDED_TO_PARENT: {
    label: "notifications:new_family_member",
    svgIcon: (
      <IcnNotiParentConnected fill={colors.white} width={32} height={32} />
    ),
    bgColor: colors.seaweedTwo,
  },
  ENTITY_RATING: {
    label: "common:likes",
    svgIcon: <IcnNotiPostLike fill={colors.white} width={32} height={32} />,
    bgColor: colors.salmon60,
  },
  ENTITY_LIKE: {
    label: "common:likes",
    svgIcon: <IcnNotiPostLike fill={colors.white} width={32} height={32} />,
    bgColor: colors.salmon60,
  },
  POST_LIKE: {
    label: "common:likes",
    svgIcon: <IcnNotiPostLike fill={colors.white} width={32} height={32} />,
    bgColor: colors.salmon60,
  },
  NEW_DISCUSSION: {
    label: "common:comment",
    svgIcon: <IcnNotiPostComment fill={colors.white} width={32} height={32} />,
    bgColor: colors.pink200,
  },
  POST_COMMENT: {
    label: "common:comment",
    svgIcon: <IcnNotiPostComment fill={colors.white} width={32} height={32} />,
    bgColor: colors.pink200,
  },
  PROGRESS_REPORT_EVALUTATION: {
    label: "assessmentEvaluation:evaluation",
    svgIcon: (
      <IcnNotiReportEvaluation fill={colors.white} width={32} height={32} />
    ),
    bgColor: colors.blueTwo,
  },
  PROGRESS_REPORT: {
    label: "notifications:progress_report_title",
    svgIcon: (
      <IcnNotiReportEvaluation fill={colors.white} width={32} height={32} />
    ),
    bgColor: colors.blueTwo,
  },
  PROGRESS_REPORT_RE_EVALUTATION: {
    label: "common:revision",
    svgIcon: (
      <IcnNotiReportRivision fill={colors.white} width={32} height={32} />
    ),
    bgColor: colors.blueTwo,
  },
  CIRCULAR_ADDED: {
    svgIcon: <IcnNotiAnnouncement fill={colors.white} width={32} height={32} />,
    bgColor: colors.teal200,
  },
  STUDENT_ASSIGNMENT_SUBMISSION: {
    label: "notifications:handed_in_submission",
    svgIcon: <SubmissionIcon fill={colors.white} width={20} height={20} />,
    bgColor: colors.teal200,
  },
  STUDENT_ASSIGNMENT_SUBMISSION_FEEDBACK: {
    label: "common:feedback",
    svgIcon: (
      <FeedbackNotificationIcon width={32} height={32} fill={colors.white} />
    ),
    bgColor: colors.teal200,
  },
  ALL_STUDENT_ASSIGNMENT_SUBMISSION: {
    label: "notifications:submission_complete",
    svgIcon: <SubmissionIcon fill={colors.white} width={20} height={20} />,
    bgColor: colors.teal200,
  },
  COMMUNITY_COMMENT: {
    label: "common:comment",
    svgIcon: <IcnNotiPostComment fill={colors.white} width={32} height={32} />,
    bgColor: colors.pink200,
  },
  COMMUNITY_COMMENT_REPLY: {
    label: "common:comment",
    svgIcon: <IcnNotiPostComment fill={colors.white} width={32} height={32} />,
    bgColor: colors.pink200,
  },
  STUDENT_ASSIGNMENT_SUBMISSION_UNHAND: {
    label: "classRoom:submission_returned_text",
    svgIcon: (
      <IcnNotiSubmissionReturned fill={colors.white} width={32} height={32} />
    ),
    bgColor: colors.yellow500,
  },
  STUDENT_ASSIGNMENT_SHARED: {
    label: "assessmentEvaluation:evaluation_complete",
    svgIcon: (
      <IcnNotiEvaluationCompleted fill={colors.white} width={32} height={32} />
    ),
    bgColor: colors.yellow500,
  },
  STUDENT_ASSESSMENT_EVALUATION: {
    label: "assessmentEvaluation:evaluation_complete",
    svgIcon: (
      <IcnNotiEvaluationCompleted fill={colors.white} width={32} height={32} />
    ),
    bgColor: colors.yellow500,
  },
  PERSONAL_PROJECT_INVITATION: {
    label: "notifications:notif_title_invited_project",
    svgIcon: <PpOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceTwoD,
  },
  COMMUNITY_PROJECT_INVITATION: {
    label: "notifications:notif_title_invited_project",
    svgIcon: <CpOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceOneD,
  },
  SERVICE_AS_ACTION_INVITATION: {
    label: "notifications:notif_title_invited_project",
    svgIcon: <SaaOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  SERVICE_AS_ACTION_ACTIVITY_APPROVAL: {
    label: "notifications:notif_title_approve_activity",
    svgIcon: <SaaOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  PERSONAL_PROJECT_STATUS_UPDATE: {
    label: "notifications:notif_title_personal_project_status_update",
    svgIcon: <PpOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceTwoD,
  },
  COMMUNITY_PROJECT_STATUS_UPDATE: {
    label: "notifications:notif_title_community_project_status_update",
    svgIcon: <CpOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceOneD,
  },
  COMMUNITY_PROJECT_REPORT_STATUS_UPDATE: {
    label: "notifications:notif_title_community_project_report_status_update",
    svgIcon: <CpOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceOneD,
  },
  PERSONAL_PROJECT_REPORT_STATUS_UPDATE: {
    label: "notifications:notif_title_personal_project_report_status_update",
    svgIcon: <PpOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceTwoD,
  },
  PROJECT_COMMENT: {
    label: "notifications:notif_title_project_comment_added_by_student",
    svgIcon: <IcnNotiPostComment fill={colors.white} width={32} height={32} />,
    bgColor: colors.pink200,
    subCategories: projectNotificationSubCategries,
  },
  PROJECT_COMMENT_MENTION: {
    label: "notifications:notif_title_project_comment_mention_added_by_student",
    svgIcon: <IcnNotiPostComment fill={colors.white} width={32} height={32} />,
    bgColor: colors.pink200,
    subCategories: projectNotificationSubCategries,
  },
  PROJECT_POST_COMMENT: {
    label: "notifications:notif_title_project_post_comment_added_by_student",
    svgIcon: <IcnNotiPostComment fill={colors.white} width={32} height={32} />,
    bgColor: colors.pink200,
    subCategories: projectNotificationSubCategries,
  },
  PORTFOLIO_COMMENT: {
    label: "notifications:notif_title_cas_portfolio_comment_added_by_student",
    svgIcon: <IcnNotiPostComment fill={colors.white} width={32} height={32} />,
    bgColor: colors.pink200,
    subCategories: projectNotificationSubCategries,
  },
  PORTFOLIO_COMMENT_MENTION: {
    label:
      "notifications:notif_title_cas_portfolio_comment_mention_added_by_student",
    svgIcon: <IcnNotiPostComment fill={colors.white} width={32} height={32} />,
    bgColor: colors.pink200,
    subCategories: projectNotificationSubCategries,
  },
  LO_COMMENT: {
    label:
      "notifications:notif_title_cas_portfolio_lo_comment_added_by_student",
    svgIcon: <IcnNotiPostComment fill={colors.white} width={32} height={32} />,
    bgColor: colors.pink200,
    subCategories: projectNotificationSubCategries,
  },
  SERVICE_AS_ACTION_IN_PROGRESS: {
    label: "notifications:notif_title_service_as_action_in_progress",
    svgIcon: <SaaOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  SERVICE_AS_ACTION_IN_PROGRESS_AFTER_APPROVAL: {
    label:
      "notifications:notif_title_service_as_action_in_progress_after_approval",
    svgIcon: <SaaOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  SERVICE_AS_ACTION_REVIEW_REQUESTED: {
    label: "notifications:notif_title_service_as_action_review_requested",
    svgIcon: <SaaOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  SERVICE_AS_ACTION_COMPLETED: {
    label: "notifications:notif_title_service_as_action_completed",
    svgIcon: <SaaOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  COMMUNITY_PROJECT_DEADLINE: {
    label: "notifications:notif_title_project_deadline_reminder",
    svgIcon: <ClockTimerOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceOneD,
  },
  PERSONAL_PROJECT_DEADLINE: {
    label: "notifications:notif_title_project_deadline_reminder",
    svgIcon: <ClockTimerOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceOneD,
  },
  DP_PROJECT_DEADLINE: {
    label: "notifications:notif_title_project_deadline_reminder",
    svgIcon: <ClockTimerOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceOneD,
  },
  PROJECT_DEADLINE_REMINDER: {
    label: "notifications:notif_title_project_deadline_reminder",
    svgIcon: <ClockTimerOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceOneD,
    subCategories: projectNotificationSubCategries,
  },
  SERVICE_AS_ACTION_PENDING_FOR_APPROVAL: {
    label: "notifications:notif_title_service_as_action_pending_for_approval",
    svgIcon: <SaaOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  UNIT_PLAN_SHARED: {
    label: "notifications:notif_title_unit_plan_shared",
    svgIcon: <StudentShareOutlined variant={"on"} size={"x-small"} />,
    bgColor: "#5f2945",
  },
  CLASS_MATERIALS_RESOURCE: {
    label: "classMaterials:class_materials",
    svgIcon: <PlaceholderFileOutlined size={"xx-small"} variant="warning" />,
    bgColor: colors.yellow900,
  },
  SNP_CHAT: {
    label: "common:comment",
    svgIcon: <IcnNotiPostComment fill={colors.white} width={32} height={32} />,
    bgColor: colors.pink200,
  },
  FILE_RESOURCE_SHARED_WITH_STUDENT: {
    label: "notifications:notif_single_file_shared_title",
    svgIcon: <PlaceholderFileOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceTwo,
  },
  FOLDER_RESOURCE_SHARED_WITH_STUDENT: {
    label: "notifications:notif_single_folder_shared_title",
    svgIcon: <FolderOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceTwo,
  },
  MULTIPLE_RESOURCE_SHARED_WITH_STUDENT: {
    label: "notifications:notif_multiple_file_shared_title",
    svgIcon: <FolderOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceTwo,
    subCategories: {
      FILE: {
        svgIcon: <PlaceholderFileOutlined variant={"on"} size={"x-small"} />,
        bgColor: colors.decorativeSurfaceTwo,
      },
      FOLDER: {
        svgIcon: <FolderOutlined variant={"on"} size={"x-small"} />,
        bgColor: colors.decorativeSurfaceTwo,
      },
    },
  },
  CAS_ADVISOR_ASSIGNED: {
    label: "notifications:notif_title_cas_advisor_assigned",
    svgIcon: <CasOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  CAS_ADVISEE_ASSIGNED: {
    label: "notifications:notif_title_cas_advisor_assigned",
    svgIcon: <CasOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  CAS_OVERALL_STATUS_CHANGED: {
    label: "notifications:notif_title_cas_overall_status_changed",
    svgIcon: <CasOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  CAS_PROJECT_DELETED: {
    label: "notifications:notif_title_cas_project_deleted",
    svgIcon: <CasOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  CAS_ADVISOR_FEEDBACK_ADDED: {
    label: "notifications:notif_title_cas_advisor_feedback_addded",
    svgIcon: <CasOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  CAS_PROJECT_APPROVAL_REQUESTED: {
    label: "notifications:notif_title_cas_project_approval_requested",
    svgIcon: <CasOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  CAS_PROJECT_CREATED: {
    label: "notifications:notif_title_cas_project_created",
    svgIcon: <CasOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  CAS_PROJECT_APPROVED: {
    label: "notifications:notif_title_cas_project_approved",
    svgIcon: <CasOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  CAS_PROJECT_REJECTED: {
    label: "notifications:notif_title_cas_project_rejected",
    svgIcon: <CasOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  CAS_LO_ACHIEVED: {
    label: "notifications:notif_title_cas_lo_achieved",
    svgIcon: <CasOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  CAS_MARKED_AS_COMPLETE: {
    label: "notifications:notif_title_cas_completed",
    svgIcon: <CasOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  ASSIGNMENT_SHARED_WITH_STUDENT: {
    label: "notifications:new_task",
    svgIcon: <IcnNotiAddedPost fill={colors.white} width={32} height={32} />,
    bgColor: colors.blue400,
    subCategories: {
      ASSIGNMENT_RESOURCE: {
        svgIcon: (
          <QuickTaskFilledIcon fill={colors.white} width={24} height={24} />
        ),
        bgColor: colors.grass,
      },
      ASSESSMENT: {
        svgIcon: <LEFilledIcon fill={colors.white} height={24} width={24} />,
        bgColor: colors.violet500,
      },
      le: {
        svgIcon: <LEFilledIcon fill={colors.white} height={24} width={24} />,
        bgColor: colors.violet500,
      },
      fmt: {
        svgIcon: <Formative fill={colors.white} height={24} width={24} />,
        bgColor: colors.yellow500,
      },
      smt: {
        svgIcon: <Summative fill={colors.white} height={24} width={24} />,
        bgColor: colors.teal500,
      },
      qt: {
        svgIcon: (
          <QuickTaskFilledIcon fill={colors.white} width={24} height={24} />
        ),
        bgColor: colors.grass,
      },
      dp_pa: {
        svgIcon: (
          <QuickTaskFilledIcon fill={colors.white} width={24} height={24} />
        ),
        bgColor: colors.grass,
      },
      dp_ep: {
        svgIcon: (
          <QuickTaskFilledIcon fill={colors.white} width={24} height={24} />
        ),
        bgColor: colors.grass,
      },
      worksheet: {
        svgIcon: (
          <QuickTaskFilledIcon fill={colors.white} width={24} height={24} />
        ),
        bgColor: colors.grass,
      },
    },
  },
  EE_SUPERVISOR_ASSIGNED: {
    label: "notifications:notif_title_ee_supervisor_assigned",
    svgIcon: <EeOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  EE_PROPOSAL_APPROVAL_REQUESTED: {
    label: "notifications:notif_title_ee_proposal_approval_requested",
    svgIcon: <EeOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  EE_PROPOSAL_APPROVED: {
    label: "notifications:notif_title_ee_proposal_approved",
    svgIcon: <EeOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  EE_FINAL_ESSAY_SUBMITTED: {
    label: "notifications:notif_title_ee_final_essay_submitted",
    svgIcon: <EeOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  EE_REFLECTION_MARKED_AS_COMPLETE: {
    label: "notifications:notif_title_ee_reflection_marked_as_complete",
    svgIcon: <EeOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  EE_MARKED_AS_COMPLETE: {
    label: "notifications:notif_title_ee_marked_as_complete",
    svgIcon: <EeOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  TOK_EXHIBITION_SUPERVISOR_ASSIGNED: {
    label: "notifications:notif_title_tok_exhibition_supervisor_assigned",
    svgIcon: <TheoryofKnowledgeOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  TOK_EXHIBITION_PROPOSAL_APPROVED: {
    label: "notifications:notif_title_tok_exhibition_proposal_approved",
    svgIcon: <TheoryofKnowledgeOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  TOK_EXHIBITION_FINAL_DRAFT_SUBMITTED: {
    label: "notifications:notif_title_tok_exhibition_final_essay_submitted",
    svgIcon: <TheoryofKnowledgeOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  TOK_EXHIBITION_MARKED_AS_COMPLETE: {
    label: "notifications:notif_title_tok_exhibition_marked_as_complete",
    svgIcon: <TheoryofKnowledgeOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  TOK_ESSAY_SUPERVISOR_ASSIGNED: {
    label: "notifications:notif_title_tok_essay_supervisor_assigned",
    svgIcon: <TheoryofKnowledgeOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  TOK_ESSAY_PROPOSAL_APPROVED: {
    label: "notifications:notif_title_tok_essay_proposal_approved",
    svgIcon: <TheoryofKnowledgeOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  TOK_ESSAY_FINAL_DRAFT_SUBMITTED: {
    label: "notifications:notif_title_tok_essay_final_essay_submitted",
    svgIcon: <TheoryofKnowledgeOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  TOK_ESSAY_MARKED_AS_COMPLETE: {
    label: "notifications:notif_title_tok_essay_marked_as_complete",
    svgIcon: <TheoryofKnowledgeOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  TOK_ESSAY_INTERACTION_MARKED_AS_COMPLETE: {
    label: "notifications:notif_title_tok_essay_interaction_marked_as_complete",
    svgIcon: <TheoryofKnowledgeOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  ATTENDANCE_REMINDER: {
    label: "attendance:attendance_reminder",
    svgIcon: <AttendanceOutlined variant="on" size={"x-small"} />,
    bgColor: colors.interactiveThreeDefault,
  },
  ATTENDANCE_REMINDER_TO_HOMEROOM: {
    label: "attendance:attendance_reminder",
    svgIcon: <AttendanceOutlined variant="on" size={"x-small"} />,
    bgColor: colors.interactiveThreeDefault,
  },
  NEW_ATTENDANCE_EXCUSAL_RECEIVED_TO_HOMEROOM: {
    label: "attendance:excusal_received",
    svgIcon: <EnvelopeClockOutlined variant="on" size={"x-small"} />,
    bgColor: colors.interactiveThreeDefault,
  },
  NEW_ATTENDANCE_EXCUSAL_RECEIVED: {
    label: "attendance:excusal_received",
    familyLabel: "attendance:excusal_requested",
    svgIcon: <EnvelopeClockOutlined variant="on" size={"x-small"} />,
    bgColor: colors.interactiveThreeDefault,
  },
  NEW_ATTENDANCE_EXCUSAL_RECEIVED_TO_PARENT: {
    label: "attendance:excusal_received",
    familyLabel: "attendance:excusal_requested",
    svgIcon: <EnvelopeClockOutlined variant="on" size={"x-small"} />,
    bgColor: colors.interactiveThreeDefault,
  },
  NEW_ATTENDANCE_EXCUSAL_RECEIVED_TO_ADMIN: {
    label: "attendance:excusal_received",
    svgIcon: <EnvelopeClockOutlined variant="on" size={"x-small"} />,
    bgColor: colors.interactiveThreeDefault,
  },
  ATTENDANCE_EXCUSAL_MESSAGE_RECEIVED_TO_HOMEROOM: {
    label: "attendance:new_message_excusal",
    svgIcon: <EnvelopeChevronRightOutlined variant="on" size={"x-small"} />,
    bgColor: colors.interactiveThreeDefault,
  },
  ATTENDANCE_MARKED_ON_EXCUSAL_TO_HOMEROOM: {
    label: "attendance:attendance_marked",
    svgIcon: <AttendanceOutlined variant="on" size={"x-small"} />,
    bgColor: colors.interactiveThreeDefault,
  },
  ATTENDANCE_EXCUSAL_MESSAGE_RECEIVED: {
    label: "attendance:new_message_excusal",
    svgIcon: <EnvelopeChevronRightOutlined variant="on" size={"x-small"} />,
    bgColor: colors.interactiveThreeDefault,
  },
  ATTENDANCE_MARKED: {
    label: "attendance:attendance_marked",
    svgIcon: <AttendanceOutlined variant="on" size={"x-small"} />,
    bgColor: colors.interactiveThreeDefault,
  },
  ATTENDANCE_MARKED_ON_EXCUSAL: {
    label: "attendance:attendance_marked",
    svgIcon: <AttendanceOutlined variant="on" size={"x-small"} />,
    bgColor: colors.interactiveThreeDefault,
  },
  PROJECT_APPROVED: {
    label: "notifications:notif_title_project_approved",
    svgIcon: <HourglassOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
    subCategories: projectNotificationSubCategries,
  },
  PROJECT_MARKED_AS_COMPLETED: {
    label: "notifications:notif_title_project_marked_as_complete",
    svgIcon: <HourglassOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
    subCategories: projectNotificationSubCategries,
  },
  PROJECT_OVERALL_STATUS_CHANGED: {
    label: "notifications:notif_title_project_status_changed",
    svgIcon: <HourglassOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
    subCategories: projectNotificationSubCategries,
  },
  PROJECT_FINAL_REPORT_SUBMITTED: {
    label: "notifications:notif_title_final_report_submitted",
    svgIcon: <HourglassOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
    subCategories: projectNotificationSubCategries,
  },
  PROJECT_DRAFT_REPORT_SUBMITTED: {
    label: "notifications:notif_title_draft_report_submitted",
    svgIcon: <HourglassOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
    subCategories: projectNotificationSubCategries,
  },
  PROJECT_SUPERVISEE_ASSIGNED: {
    label: "notifications:notif_title_project_supervisee_assigned",
    svgIcon: <HourglassOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
    subCategories: projectNotificationSubCategries,
  },
  PROJECT_SUPERVISOR_ASSIGNED: {
    label: "notifications:notif_title_project_supervisor_assigned",
    svgIcon: <HourglassOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
    subCategories: projectNotificationSubCategries,
  },
  PROJECT_REMOVED_AS_COLLABORATOR: {
    label: "notifications:notif_title_project_removed_as_collaborator",
    svgIcon: <HourglassOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
    subCategories: projectNotificationSubCategries,
  },
  DP_EXAM_APPROVAL: {
    label: "notifications:notif_exam_reg_approval_title",
    svgIcon: <ClipboardPencilLinesOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceOneD,
  },
  INTEGRATION_LONG_RUNNING_NOTIFICATION: {
    label: "notifications:notif_long_running_completion",
    svgIcon: <ImportOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFive,
  },
  PROJECT_REPORT_STATUS_UPDATED: {
    label: "notifications:notif_title_report_status_updated",
    svgIcon: <HourglassOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
    subCategories: projectNotificationSubCategries,
  },
  PROJECT_ATTACHMENT_ANNOTATED: {
    label: "notifications:notif_title_project_attachment_annotated",
    svgIcon: <HourglassOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
    subCategories: projectNotificationSubCategries,
  },
  PENDING_FAMILY_EMAIL_INVITATION: {
    label: "notifications:notif_pending_family_email_invite_title",
    svgIcon: <StudentOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  REJECTED_FAMILY_CLASSLINK_INVITATION: {
    label: "notifications:notif_rejected_family_class_invite_title",
    svgIcon: <EnvelopeDotFilledOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
  },
  PROJECT_GROUP_SUPERVISOR_APPROVAL_REQUESTED: {
    label: "notifications:notif_title_saa_supervisor_approval_requested",
    svgIcon: <HourglassOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
    subCategories: projectNotificationSubCategries,
  },
  PROJECT_GROUP_SUPERVISOR_APPROVAL_REQUEST_REJECTED: {
    label:
      "notifications:notif_title_project_group_supervisor_approval_request_rejected",
    svgIcon: <HourglassOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
    subCategories: projectNotificationSubCategries,
  },
  PROJECT_ACADEMIC_HONESTY_SUBMITTED: {
    label: "notifications:notif_title_dp_ee_project_academic_honesty_submitted",
    svgIcon: <HourglassOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
    subCategories: projectNotificationSubCategries,
  },
  PROJECT_ACADEMIC_HONESTY_LOCKED: {
    label: "notifications:notif_title_g4_project_academic_honesty_locked",
    svgIcon: <HourglassOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
    subCategories: projectNotificationSubCategries,
  },
  PROJECT_ACADEMIC_HONESTY_UNLOCKED: {
    label: "notifications:notif_title_g4_project_academic_honesty_unlocked",
    svgIcon: <HourglassOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
    subCategories: projectNotificationSubCategries,
  },
  PROJECT_REVIEW_LOG_ADDED: {
    label: "notifications:notif_title_project_review_log_added",
    svgIcon: <HourglassOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
    subCategories: projectNotificationSubCategries,
  },
  PROJECT_REVIEW_LOG_COMMENT_ADDED: {
    label: "notifications:notif_title_project_review_log_comment_added",
    svgIcon: <HourglassOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
    subCategories: projectNotificationSubCategries,
  },
  PROJECT_REVIEW_LOG_RESOLVED: {
    label: "notifications:notif_title_project_review_log_resolved",
    svgIcon: <HourglassOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
    subCategories: projectNotificationSubCategries,
  },
  PROJECT_REVIEW_LOG_UNRESOLVED: {
    label: "notifications:notif_title_project_review_log_unresolved",
    svgIcon: <HourglassOutlined variant={"on"} size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFiveD,
    subCategories: projectNotificationSubCategries,
  },
  PDFTRON_COMMENT_ADDED: {
    label: "classRoom:pdftron_comment_added_text",
    svgIcon: <SubmissionIcon fill={colors.white} width={20} height={20} />,
    bgColor: colors.teal200,
  },
};

const styles = {
  loadingStyle: { width: 28, height: 28 },
};

class ListContainer extends React.PureComponent {
  constructor(props) {
    super(props);
    if (props.customRef) {
      props.customRef(this);
    }

    this.state = {
      isLoading: false,
    };
  }

  fetchMoreNotifications = e => {
    const container = this.container;
    if (
      this.props.hasNextPage &&
      this.props.getNotifications.networkStatus != 3 &&
      checkScrollAtBottom(container)
    ) {
      this.props.loadMoreNotifications();
    }
    if (this.props.onScroll) {
      this.props.onScroll(e);
    }
  };

  scrollToTop = () => {
    if (this.container) {
      this.container.scrollTop = 0;
    }
  };

  getCollaboratorCoursesForNotification = async notification => {
    const {
      userId,
      courses,
      currentCurriculumProgram,
      getUserCoursesBasicDetails,
    } = this.props;

    // find notification curriculum program
    const notificationCourse = _.head(_.get(notification, "courses", []));
    const notificationCourseId = _.get(notificationCourse, "id", null);
    const notificationCurriculumProgramId = _.get(
      notificationCourse,
      "curriculumProgram.id",
      null
    );

    let courseIdToRedirect;
    let courseToRedirect;
    let notificationCurriculumProgramCourses;
    let isUnitLibraryRoute = false;

    // if user's current curriculum program does not match notfiication curriculum program
    if (currentCurriculumProgram?.id !== notificationCurriculumProgramId) {
      // fetch user's courses for the notification curriculum program
      this.setState({ isLoading: true });
      const otherCurriculumProgramCourses = await getUserCoursesBasicDetails({
        userId,
        courseFilters: {
          curriculumProgramIds: [notificationCurriculumProgramId],
          archivalState: "ACTIVE",
        },
      });
      this.setState({ isLoading: false });
      // use above fetched courses to find single course to redirect
      notificationCurriculumProgramCourses = otherCurriculumProgramCourses;
    } else {
      // use current curriculum user's courses to find single course to redirect
      notificationCurriculumProgramCourses = courses;
    }

    const isStaffInCourse = !_.isEmpty(
      _.find(notificationCurriculumProgramCourses, {
        id: notificationCourseId,
      })
    );

    if (isStaffInCourse) {
      // if staff is part of the notification course, then redirect to that course
      courseToRedirect = notificationCourse;
      courseIdToRedirect = notificationCourseId;
    } else {
      // if staff is not a part of the notification course,
      // then redirect to the first course from user's courses inside notification curriculum program
      courseToRedirect = _.head(notificationCurriculumProgramCourses);
      courseIdToRedirect = _.get(courseToRedirect, "id");
      isUnitLibraryRoute = true;
    }
    return { courseIdToRedirect, courseToRedirect, isUnitLibraryRoute };
  };

  getNotificationCourseToRedirect = async notification => {
    const notificationType = _.get(notification, "type", "");

    // edge case for notification type "COLLABORATOR"
    // remove below code on future if unit library moves from class to home page
    if (notificationType === "COLLABORATOR") {
      return await this.getCollaboratorCoursesForNotification(notification);
    }

    const { courses, selectedCourseId, sourceFrom } = this.props;

    // current user curriculum course ids
    const userCourseIds = _.map(courses, course => _.get(course, "id"));

    // if notification is having multiple courses
    const notificationCourseIds = _.get(notification, "metadata.courses");

    // if notification is having single course, then one of the below two will contain that course id
    const notificationCourseId = _.get(notification, "metadata.courseId");
    const notificationCourse_id = _.get(notification, "metadata.course_id");

    // if notification center is opened inside any course
    const userSelectedCourseId =
      sourceFrom == "sidebar" ? selectedCourseId : "";

    const commonCourseIds = getUserNotificationCommonCourseIdsMemoize({
      userCourseIds,
      notificationCourseIds,
    });

    // choose course id to redirect when notification is clicked
    const courseIdToRedirect =
      _.first(commonCourseIds) ||
      userSelectedCourseId ||
      notificationCourseId ||
      notificationCourse_id ||
      _.head(userCourseIds);

    const courseToRedirect = _.find(
      _.get(notification, "courses", []),
      course => course.id == courseIdToRedirect
    );

    return { courseIdToRedirect, courseToRedirect };
  };

  onNotificationClicked = async notification => {
    const {
      onNotificationClicked,
      navigate,
      notificationCenterReadMutation,
      userId,
      courses,
      toggleShowNotificationCenter,
    } = this.props;

    //Over riding user courseId in case of notification having multiple course.
    const cloneNotification = _.cloneDeep(notification);

    // current user curriculum course ids
    const userCourseIds = _.map(courses, course => _.get(course, "id"));

    const {
      courseIdToRedirect,
      courseToRedirect,
      isUnitLibraryRoute = false,
    } = await this.getNotificationCourseToRedirect(notification);

    cloneNotification.metadata = {
      ...(cloneNotification.metadata || {}),
      courseId: courseIdToRedirect,
      userCourseIds,
      isUnitLibraryRoute,
    };

    notificationCenterReadMutation({
      userId,
      notificationId: cloneNotification.id,
    });

    if (!_.isEmpty(courseToRedirect)) {
      this.syncPlatformCurriculum({ course: courseToRedirect });
    }

    navigate({
      params: cloneNotification,
      target: "notification",
    });

    if (onNotificationClicked) {
      onNotificationClicked(cloneNotification);
    }

    // close notification center if opened from notification tray
    if (toggleShowNotificationCenter) {
      toggleShowNotificationCenter();
    }
  };

  syncPlatformCurriculum = ({ course }) => {
    const {
      currentCurriculumProgram,
      setCurrentCurriculumProgram,
    } = this.props;
    const notificationCurriculumProgram = _.get(
      course,
      "curriculumProgram",
      {}
    );

    if (
      currentCurriculumProgram.id != notificationCurriculumProgram.id &&
      !_.isEmpty(notificationCurriculumProgram)
    ) {
      setCurrentCurriculumProgram(notificationCurriculumProgram);
    }
  };

  renderNotifications = () => {
    const {
      props: { notifications, user_type },
    } = this;

    return (
      <div
        ref={ref => (this.containerDiv = ref)}
        className={classes.notificationsContainer}
      >
        {_.get(notifications, "length", 0) > 0 ? (
          <List
            data={notifications}
            user_type={user_type}
            onNotificationClicked={this.onNotificationClicked}
            notificationTypes={notificationTypes}
          />
        ) : null}
        {this.props.getNotifications.networkStatus == 3 && (
          <div className={classes.loadingContainer}>
            <Loading loadingStyle={styles.loadingStyle} />
          </div>
        )}
      </div>
    );
  };

  renderEmptyState = () => {
    return (
      <div className={classes.emptyState}>
        {this.props.t("notifications:no_notifications")}
      </div>
    );
  };

  render() {
    const {
      props: { notifications },
    } = this;

    const { isLoading } = this.state;

    return (
      <div
        onScroll={this.fetchMoreNotifications}
        className={classes.container}
        ref={ref => (this.container = ref)}
      >
        {_.get(notifications, "length", 0) > 0
          ? this.renderNotifications()
          : this.renderEmptyState()}
        {isLoading ? <FullScreenLoader /> : null}
      </div>
    );
  }

  componentDidMount = () => {
    setTimeout(() => {
      if (!this.props.isLoading && this.props.showNotificationCenter) {
        this.props.notificationCenterOpenMutation({
          userId: this.props.userId,
          activeTabFilterValue: this.props.activeTabFilterValue,
        });
      }
    });
  };

  componentWillUnmount = () => {
    this.props.getNotifications.refetch();
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      ((!nextProps.isLoading && this.props.isLoading) ||
        !_.isEqual(
          nextProps.activeTabFilterValue,
          this.props.activeTabFilterValue
        )) &&
      this.props.showNotificationCenter
    ) {
      setTimeout(() => {
        this.props.notificationCenterOpenMutation({
          userId: this.props.userId,
          activeTabFilterValue: nextProps.activeTabFilterValue,
        });
      });
    }
    if (
      nextProps.showNotificationCenter !== this.props.showNotificationCenter
    ) {
      if (this.container) {
        this.container.scrollTop = 0;
      }
      // this.props.getNotifications.refetch();
    }
  }
}

const mapActionCreators = {
  notificationCenterOpenMutation,
  notificationCenterReadMutation,
  setNotificationCount,
  navigate,
  setCurrentCurriculumProgram,
  getUserCoursesBasicDetails,
};

const mapStateToProps = (state, ownProps) => {
  const userInfo = getUserInfo();
  const selectedCourseId = _.get(
    state,
    "teacher.selected_class.selected_course"
  );
  const currentCurriculumProgram = _.get(
    state,
    "platform.currentCurriculumProgram"
  );

  return {
    userId: userInfo.id,
    userEntityType: userInfo.userEntityType,
    user_type: userInfo.user_type,
    selectedCourseId,
    currentCurriculumProgram,
  };
};

export default compose(
  I18nHOC({ resource: ["notifications"] }),
  connect(mapStateToProps, mapActionCreators),
  graphql(getNotificationsQuery, {
    name: "getNotifications",
    options: ({ userId, activeTabFilterValue, userEntityType }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: userId,
        type: userEntityType,
        notificationCategoryFilter: activeTabFilterValue,
      },
    }),
    props({
      getNotifications,
      ownProps: { userId, activeTabFilterValue, userEntityType },
    }) {
      const notificationData = getNotificationListFromCache({
        id: userId,
        type: userEntityType,
        notificationCategoryFilter: activeTabFilterValue,
      });

      const notification = _.get(notificationData, "notification", []);

      const isLoading =
        getNotifications["networkStatus"] == 1 ||
        getNotifications["networkStatus"] == 2;
      return {
        getNotifications,
        isData: !_.isEmpty(notification),
        notifications: _.map(
          _.get(notification, "edge", []),
          item => item.node
        ),
        isLoading:
          getNotifications["networkStatus"] == 1 ||
          getNotifications["networkStatus"] == 2 ||
          getNotifications["networkStatus"] == 4,
        hasNextPage: _.get(
          getNotifications.node,
          "notification.pageInfo.hasNextPage",
          false
        ),
        onPusherEventTriggered: () => getNotifications.refetch(),
        loadMoreNotifications: () => {
          const endCursor = _.get(
            getNotifications.node,
            "notification.pageInfo.endCursor",
            null
          );

          return getNotifications.fetchMore({
            variables: {
              after: endCursor,
              id: userId,
              type: userEntityType,
              notificationCategoryFilter: activeTabFilterValue,
            },
            updateQuery: (previousResult, { fetchMoreResult }) => {
              const previousNotifications =
                previousResult.node.notification.edge;
              const newNotifications = fetchMoreResult.node.notification.edge;

              return {
                ...previousResult,
                node: {
                  ...previousResult.node,
                  notification: {
                    ...previousResult.node.notification,
                    pageInfo: {
                      ...previousResult.node.notification.pageInfo,
                      ...fetchMoreResult.node.notification.pageInfo,
                    },
                    edge: [...previousNotifications, ...newNotifications],
                  },
                },
              };
            },
          });
        },
      };
    },
  }),
  withPusherBind(["notificationAdded"]),
  withLoader
)(ListContainer);
